import { Button, Input, Skeleton, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Field } from '../../components/field';
import { ApiError } from '../../lib/api';
import type { Company } from './companies.api';
import { invoiceSeriesQueryKey, invoiceSeriesQueryOptions, saveSeries } from './invoice-series.api';

/**
 * Secțiunea „Facturare" din setările firmei: seria de facturi și numărul de la care pornește
 * ([#188](https://github.com/bplugaru/efx-pcrm/issues/188)).
 *
 * **Se salvează pe loc**, separat de formular, ca logo-ul: seria stă în modulul de
 * facturare, nu pe rândul firmei, iar butonul „Salvează" al fișei n-o poartă.
 *
 * **O singură serie, fără probă și fără confirmare dublă** (fondatorul, 2026-10-12, #201: *„le
 * modific acolo și asta e"*):
 *
 * - un cod nou e o serie nouă, care pornește de la numărul scris;
 * - un cod folosit înainte **reia** seria aceea de la ultimul ei număr — câmpul de start se
 *   blochează și arată de unde continuă, ca nimeni să nu creadă că pornește din nou de la 1;
 * - startul se mută doar cât seria n-are facturi.
 */
export function InvoiceSeriesSection({
  company,
  disabled,
}: {
  company: Company;
  disabled: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery(invoiceSeriesQueryOptions(company.id));
  const active = query.data?.series ?? null;

  const [code, setCode] = useState('');
  const [start, setStart] = useState('1');
  const [error, setError] = useState<{ field: 'code' | 'startNumber'; message: string } | null>(
    null,
  );

  // Alt rând, sau seria s-a schimbat pe server: formularul pleacă de la ce e acum în bază.
  useEffect(() => {
    setCode(active?.code ?? '');
    setStart(String(active?.startNumber ?? 1));
    setError(null);
  }, [active]);

  const save = useMutation({
    mutationFn: (startNumber: number) => saveSeries({ companyId: company.id, code, startNumber }),
    onSuccess: async (saved) => {
      toast.success(`Firma emite pe seria ${saved.code}, de la ${saved.nextNumber}`);
      await queryClient.invalidateQueries({ queryKey: invoiceSeriesQueryKey(company.id) });
    },
    onError: (failure: unknown) => {
      if (failure instanceof ApiError) {
        const issue = failure.issues[0];

        if (failure.code === 'invoice_series_start_locked') {
          setError({ field: 'startNumber', message: failure.message });
          return;
        }

        if (issue && (issue.path === 'code' || issue.path === 'startNumber')) {
          setError({ field: issue.path, message: issue.message });
          return;
        }
      }

      toast.error(failure instanceof ApiError ? failure.message : 'Seria nu a putut fi salvată.');
    },
  });

  if (query.isPending) {
    return <Skeleton className="h-16 w-full" />;
  }

  if (query.error) {
    return (
      <Text size="small" className="text-ui-fg-error">
        {query.error instanceof Error ? query.error.message : 'Seria nu a putut fi citită.'}
      </Text>
    );
  }

  // Seria cu codul scris, dacă a existat: cu facturi emise, numerotarea ei continuă, nu repornește.
  const known = query.data.all.find((series) => series.code === code) ?? null;
  const locked = known !== null && known.lastNumber !== null;
  const startNumber = locked ? known.startNumber : parseStart(start);
  const changed = active === null || code !== active.code || startNumber !== active.startNumber;
  const busy = disabled || save.isPending;
  const canSave = !busy && changed && code.length > 0 && startNumber !== null;

  return (
    <div className="flex flex-col gap-y-4">
      {active ? (
        <Text size="small" className="text-ui-fg-subtle">
          Următoarea factură:{' '}
          <span className="font-mono text-ui-fg-base">
            {active.code} {active.nextNumber}
          </span>
        </Text>
      ) : (
        <Text size="small" className="text-ui-fg-subtle">
          Firma n-are încă serie de facturi.
        </Text>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Serie"
          id="invoice-series-code"
          error={error?.field === 'code' ? error.message : undefined}
        >
          <Input
            id="invoice-series-code"
            value={code}
            onChange={(event) => {
              setCode(event.target.value.toUpperCase());
              setError(null);
            }}
            placeholder="EFX"
            maxLength={10}
            className="font-mono"
            disabled={busy}
          />
        </Field>

        <Field
          label={locked ? 'Continuă de la' : 'Număr de start'}
          id="invoice-series-start"
          error={error?.field === 'startNumber' ? error.message : undefined}
          hint={
            locked
              ? `Seria ${known.code} are deja facturi: numerotarea continuă de la ultima.`
              : known
                ? `Seria ${known.code} a mai fost folosită, dar n-are facturi.`
                : undefined
          }
        >
          {/*
            `type="text"`, nu `type="number"`, ca la prețul pachetului: pe `number`, rotița
            mouse-ului schimbă valoarea peste care trece cursorul — aici, numărul de la care
            pornește o serie de facturi.
          */}
          <Input
            id="invoice-series-start"
            inputMode="numeric"
            value={locked ? known.nextNumber : start}
            onChange={(event) => {
              setStart(event.target.value.trim());
              setError(null);
            }}
            className="font-mono tabular-nums"
            disabled={busy || locked}
          />
        </Field>
      </div>

      <div>
        <Button
          size="small"
          variant="secondary"
          onClick={() => startNumber !== null && save.mutate(startNumber)}
          isLoading={save.isPending}
          disabled={!canSave}
        >
          Salvează seria
        </Button>
      </div>
    </div>
  );
}

/** Numărul de start din câmpul de text: un întreg pozitiv, sau `null`. */
function parseStart(value: string): number | null {
  return /^[1-9][0-9]*$/.test(value) ? Number(value) : null;
}
